
<script setup>
    // Offline read-only — the store refuses these writes; the control must
    // not look live. See docs/superpowers/specs/2026-09-11-offline-read-only-design.md.
    const { isOnline } = useOnlineStatus();
    // Ties the visible offline note to the disabled buttons; a title tooltip
    // alone never shows on touch.
    const offlineNoteId = useId();
    // Libraries
    import { storeToRefs } from 'pinia';

    // Stores
    import { useUserStore } from '@/stores/user';
    const userStore = useUserStore();

    import { useSitesStore } from '@/stores/sites';
    const sitesStore = useSitesStore();

    // Composables
    const { updateOhcList } = useUtils();
    const { $listen } = useNuxtApp();

    // Emits
    const emit = defineEmits(['closeEdit', 'updateGhostItinerary']);

    // Props
    const props = defineProps({
        itinerary: {
            type: Object,
            required: false,
            default: {},
        },
        cards: {
            type: Array,
            required: false,
            default: [],
        },
    });

    const route = useRoute();
    const { itinerary, cards } = toRefs(props);
    const isFramed = ref(route.path.includes('iframe'));
    // Variables
    const { sitesData: sites } = storeToRefs(sitesStore);
    const { ghostItinerary, ghostBaseItinerary, itineraries } = storeToRefs(userStore);
    const itineraryName = ref(ghostBaseItinerary.value.name);
    // const ghostCards = ref([]);
    const removedSiteNames = ref([]);
    const ohcItineraries = ref([]);
    const updatedItineraryName = ref(null);
    const duplicateItinerary = ref(false);
    const characterLimitWarning = ref(false);
    const updatedItineraryList = ref(null);
    const addSites = ref(false);
    const nameChanged = ref(false);
    const isDeleteModalOpen = ref(false);
    const isCancelModalOpen = ref(false);

    // Computed
    const placeholder = computed(() => {
        if (itinerary && itinerary?.value?.name) {
            return itinerary.value.name;
        }
        return 'No name for this itinerary!';
    });

    const listChanged = computed(() => {
        const stringifyItinerary = JSON.stringify(ghostItinerary.value);
        const stringifyBaseItinerary = JSON.stringify(ghostBaseItinerary.value);

        let change = false;
        if (stringifyItinerary !== stringifyBaseItinerary) {
            change = true;
        }

        if (itineraryName.value !== ghostBaseItinerary.value.name) {
            change = true;
        }

        return change;
    });

    const ghostCards = computed(() => {
        const ghostCards = [];
        const ghostSiteIds = itinerary.value.sites;

        for (let i = 0; i < ghostSiteIds.length; i++) {
            const found = sites.value.find((site) => site.id === ghostSiteIds[i]);
            if (found) {
                ghostCards.push(found);
            }
        }

        return ghostCards;
    });

    // Methods
    function close() {
        if (addSites.value) {
            closeAddSites();
        } else {
            if (listChanged.value) {
                isCancelModalOpen.value = true;
            } else {
                emit('closeEdit');
            }
        }
    }

    function cancel() {
        userStore.resetGhostItinerary();
        emit('closeEdit');
    }

    function closeSiteAlert() {
        updatedItineraryList.value = null;
        return updatedItineraryList;
    }

    function closeNameAlert() {
        updatedItineraryName.value = null;
        itineraryName.value = '';
        return updatedItineraryName;
    }

    function editItineraryName(clonedList) {
        clonedList.forEach((item, index) => {
            if (item.name === itinerary.value.name) {
                clonedList[index].name = itineraryName.value;
            }
        });

        updateOhcList('ohcItineraries', clonedList);

        itinerary.value.name = itineraryName.value;
        updatedItineraryName.value = itineraryName.value;
    }

    function handleClick() {
        if (listChanged.value) {
            // Handle Name Collisions
            if (itineraryName.value !== ghostBaseItinerary.value.name) {
                let validName = true;
                if (itineraryName.value.length < 3 || itineraryName.value.length > 40) {
                    validName = false;
                    characterLimitWarning.value = true;
                    return;
                }

                // Check for duplicate itinerary names
                let isDuplicate = false;

                for (let i = 0; i < itineraries.value.length; i++) {
                    if (itineraries.value[i].name === itineraryName.value) {
                        isDuplicate = true;
                        break;
                    }
                }

                if (isDuplicate) {
                    duplicateItinerary.value = true;
                    return;
                }
            }

            // Update the itinerary
            const name = itineraryName.value;
            const sites = ghostItinerary.value.sites;
            const originalName = ghostBaseItinerary.value.name;
            userStore.updateSingleItinerary(originalName, name, sites);

            // Saved, so close outright. This used to call close(), which
            // re-checks listChanged — and listChanged only clears if
            // ghostBaseItinerary is the very object updateSingleItinerary just
            // mutated. A server sync replaces `itineraries` with fresh objects,
            // after which it never is: Apply saved, then raised "Changes not
            // applied" (O2-54). Re-base the ghost on what was saved instead.
            const saved = { ...ghostItinerary.value, name, sites: [...sites] };
            userStore.setGhostBaseItinerary(saved);
            userStore.setGhostItinerary(saved);
            emit('closeEdit');
        } else {
            console.error('No changes to save');
        }
    }

    function openAddSites() {
        addSites.value = true;
    }

    function closeAddSites() {
        addSites.value = false;

        // close();
    }

    function startDelete() {
        isDeleteModalOpen.value = true;
    }

    function closeDelete() {
        isDeleteModalOpen.value = false;
    }

    function closeCancelModal() {
        isCancelModalOpen.value = false;
    }

    function showCancelModal() {
        if (listChanged.value) {
            isCancelModalOpen.value = true;
        } else {
            close();
        }
    }

    function updateSingleItem(item) {
        const parsedItem = JSON.parse(item);

        let method = 'add';
        if (itinerary.value.sites.includes(parsedItem)) {
            method = 'remove';
        }

        let clone = JSON.parse(JSON.stringify(itinerary.value));

        switch (method) {
            case 'add':
                clone.sites.push(parsedItem);
                break;
            case 'remove':
                clone.sites = clone.sites.filter((site) => site !== parsedItem);
                break;
        }
        emit('updateGhostItinerary', clone);
    }

    // Lifecycle
    // The action bar is fixed over the bottom of the scrolling content, so the
    // content has to reserve exactly its height or the last thing in the list
    // sits underneath it. That was a hardcoded 120px, which fitted while the bar
    // held only Apply. Adding Cancel (O2-44) made it two stacked buttons and
    // 149px on a phone, so Delete Itinerary ended up behind it (Asana O2-63).
    //
    // Measured rather than guessed, so a third button — or a longer label
    // wrapping to two lines — cannot reintroduce this.
    const actionBar = ref(null);
    const editRoot = ref(null);
    let barObserver = null;

    // Escape does what the back caret does: leaves Add Sites, or asks before
    // throwing away unapplied changes, or closes. The confirmations and the
    // Add Sites filter panel are dialogs of their own stacked on this one, so
    // while one is open Escape goes to it instead.
    useDialog(editRoot, { onEscape: close });

    const syncActionBarHeight = () => {
        const height = actionBar.value?.getBoundingClientRect().height ?? 0;
        editRoot.value?.style.setProperty('--action-bar-height', `${Math.ceil(height)}px`);
    };

    onMounted(() => {
        if (import.meta.client) {
            $listen('itineraryDeleted', () => {
                close();
            });

            syncActionBarHeight();
            if (window.ResizeObserver && actionBar.value) {
                barObserver = new ResizeObserver(syncActionBarHeight);
                barObserver.observe(actionBar.value);
            }
        }
    });

    onBeforeUnmount(() => {
        barObserver?.disconnect();
        barObserver = null;
    });

    // v-show toggles the bar between display:none and visible, which changes its
    // measured height to 0 and back.
    watch(addSites, () => nextTick(syncActionBarHeight));

    watch(itineraryName, (newVal, oldVal) => {
        duplicateItinerary.value = false;
        characterLimitWarning.value = false;
        if (itineraryName.value.length) {
            nameChanged.value = true;
        } else {
            nameChanged.value = false;
        }
    });
</script>
<template>
    <!-- Modal: useDialog moves focus in, keeps Tab inside, closes on Escape
         and hands focus back to the Edit button. -->
    <div class="itinerary-edit" :class="{ framed: isFramed }" ref="editRoot" role="dialog" aria-modal="true" aria-labelledby="edit-itinerary-title" tabindex="-1">
        <div class="container">
            <div class="first-view" v-show="!addSites">
                <header>
                    <!-- Was labelled "Close Filters", copied from the filter panel. -->
                    <button type="button" class="back-caret" @click.prevent="close" aria-label="Close">
                        <SvgsBackCaret />
                    </button>
                    <h2 id="edit-itinerary-title" class="subhead-2">Edit Itinerary</h2>
                </header>

                <form>
                    <fieldset class="form-field">
                        <label for="edit-name" class="card-header"> Itinerary Name </label>
                        <input v-model="itineraryName" type="text" id="edit-name" />
                        <p v-show="characterLimitWarning" class="required red">Name must be between 3 and 40 characters.</p>
                        <p v-show="duplicateItinerary" class="required red">Duplicate entry. Please use another name.</p>
                    </fieldset>
                </form>
                <div>
                    <GhostSitesListStack :cards="ghostCards" componentLocation="itinerary" :itinerary="itinerary" @updateSingleItem="updateSingleItem" />
                    <div>
                        <button @click.prevent="openAddSites" class="button black add-site">
                            <SvgsAdd />
                            Add Site to Itinerary
                        </button>
                    </div>
                </div>
                <div class="buttons-wrapper">
                    <p v-if="!isOnline" :id="offlineNoteId" class="required">Unavailable offline. Reconnect to change this itinerary.</p>
                    <button @click.prevent="startDelete" :disabled="!isOnline" :title="!isOnline ? 'Unavailable offline' : undefined" :aria-describedby="!isOnline ? offlineNoteId : undefined" class="text-button delete-itinerary-button">Delete Itinerary</button>
                </div>
            </div>
            <!-- Cancel sits beside Apply rather than stacked above it in the body,
                 matching how the Create Itinerary modal pairs its two actions
                 (Asana O2-44). It lives here rather than in .first-view so it can
                 share the action bar, hence the explicit v-show — this wrapper is
                 outside .first-view and would otherwise show during Add Sites.

                 The v-show is on the WRAPPER, not the Cancel button (Asana
                 O2-54). Hiding only the button left this bar — position: fixed,
                 z-index: 9999, full width along the bottom — sitting over the
                 Add Sites filter panel, whose own Apply/Reset bar is also fixed
                 at 9999. Two overlapping action bars at the same z-index, so the
                 filter's buttons could not be clicked at all. Apply is not
                 needed during Add Sites: closing that panel returns to this view
                 with the bar and any pending changes intact. -->
            <div class="apply-button-wrapper" v-show="!addSites" ref="actionBar">
                <div class="container">
                    <button @click.prevent="showCancelModal" class="apply-cancel-buttons cancel-button">Cancel</button>
                    <button @click.prevent="handleClick" :disabled="!listChanged || !isOnline" :title="!isOnline ? 'Unavailable offline' : undefined" :aria-describedby="!isOnline ? offlineNoteId : undefined" :class="{ active: listChanged && isOnline }" class="apply-cancel-buttons apply-button white-to-turquoise">
                        Apply
                    </button>
                </div>
            </div>
            <ItinerariesAddSites v-if="addSites" @closeAddSites="closeAddSites" @updateSingleItem="updateSingleItem" :itinerary="itinerary" />
            <ItinerariesEditedNameAlert v-if="updatedItineraryName !== null" @closeEditedNameAlert="closeNameAlert" :listName="updatedItineraryName" />
            <ItinerariesRemovedSiteAlert v-if="updatedItineraryList !== null" @closeRemovedSiteAlert="closeSiteAlert" :listName="itinerary.name" :siteNames="removedSiteNames" />
            <ItinerariesWhiteCardAlert v-if="isDeleteModalOpen" @closeDelete="closeDelete" :itinerary="itinerary" />
            <ItinerariesWhiteCardCancel v-if="listChanged && isCancelModalOpen" @closeCancelModal="closeCancelModal" @close="cancel" />
        </div>
    </div>
</template>
<style lang="scss" scoped>
    .itinerary-edit {
        z-index: 9999;

        // Focus lands on the dialog itself when it opens.
        &:focus {
            outline: none;
        }

        overflow: scroll;
        position: fixed;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        background-color: $white;

        @media (min-width: $tablet) {
            width: 70%;
            max-width: $hd;
            margin: auto;
            height: 80%;
            top: 0px;
            bottom: 0px;
            overflow-x: auto;
            overflow-y: auto;
        }

        > .container {
            // The full height of the fixed chrome, not just the header. This was
            // $header_height (59px), which predates the mobile chip bar — that
            // bar is position: fixed at top: 59px and 52px tall, so it covered
            // the top 16px of the "Edit Itinerary" heading and sliced it in half
            // (Asana O2-71). --sticky-bar-height is the layout's own sum of
            // header + chip bar + nav row, so this stays right as the chrome
            // changes across breakpoints. Fallback for layouts that do not
            // define it.
            margin-top: var(--sticky-bar-height, #{$header_height});
            padding: 16px;            
            padding-top: 36px;
            // Matches the fixed action bar's real height plus breathing room —
            // see syncActionBarHeight. The fallback covers the first paint,
            // before the bar has been measured.
            padding-bottom: calc(var(--action-bar-height, 120px) + 24px);
            background-color: white;

            @media (min-width: $tablet) {
                padding: 64px 64px calc(var(--action-bar-height, 96px) + 64px) 64px;
                margin-top: 0;
            }
        }

        &::before {
            content: '';
            display: inline-block;
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 59px;
            background: $gray-darkest;
            background-blend-mode: multiply;
            opacity: 0.7;

            @media (min-width: $tablet) {
                height: 0px;
            }
        }

        .ghost-sites-list-stack {
            margin: 32px auto;
        }

        .buttons-wrapper {
            margin-top: 16px;
            text-align: center;
            @media (min-width: $tablet) {
                margin-top: 32px;
            }
        }

        [iframe='true'],
        &.framed {
            .apply-button-wrapper {
                @supports (-webkit-touch-callout: none) {
                    bottom: 70px;
                }
            }
        }

        .apply-button-wrapper {
            background-color: $white;
            position: fixed;
            padding: 16px;
            // Fixed to the bottom of the screen: keep Apply and Cancel clear
            // of the iPhone home indicator. The bar's height is measured
            // (syncActionBarHeight), so the content padding follows.
            padding-bottom: calc(16px + env(safe-area-inset-bottom));
            left: 0;
            bottom: 0;
            width: 100%;
            height: auto;
            z-index: 9999;
            box-shadow: 0 -2px 6px rgba(0, 0, 0, 0.1);            

            @media (min-width: $tablet) {
                margin: 0px auto;
                width: 70%;
                left: 50%;
                margin-top: 30px;
                max-width: $hd;
                transform: translate(-50%);
                width: 70%;
                margin-bottom: 10vh;
            }

            // Cancel and Apply share this bar side by side, the same pairing the
            // Create Itinerary modal uses. They stack on narrow screens where
            // there isn't room for two full-width pills.
            > .container {
                display: flex;
                flex-direction: column;
                gap: 12px;

                @media (min-width: $tablet) {
                    flex-direction: row;
                    align-items: center;
                    justify-content: center;
                    gap: 20px;
                }

                .cancel-button,
                .apply-button {
                    @media (min-width: $tablet) {
                        width: 40%;
                        margin: 0;
                    }
                }
            }
        }

        @media (min-width: $tablet) {
            button.add-site {
                max-width: 300px;
            }
        }
    }

    header {
        position: relative;
        text-align: center;
        margin-bottom: 34px;

        .reset {
            position: absolute;
            right: 0;
            top: 0;
        }

        .back-caret {
            position: absolute;
            left: 0;
            top: 0;
        }
    }

    form {
        display: flex;
        flex-direction: column;
        margin-bottom: 16px;

        label {
            display: block;
            margin-bottom: 12px;
        }
    }
</style>